import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import GlobalLayout from 'components/Layouts/GlobalLayout';
import { mediaQuery } from 'components';
import { SEO } from 'components/SEO';
import Breadcrumbs from 'components/Breadcrumbs';
import { TrackingProp } from 'react-tracking';
import { PDPProvider, PDP, BottomContent } from 'features/pdp';
import { Product, RelatedProduct, Rating } from 'types/Product';
import Helmet from 'react-helmet';
import { analyticsWrapper } from 'analytics/itly/analyticsWrapper';
import { useTrackPageViewed, useTrackProductViewed, useTrackGleamEmailSubmitted } from 'analytics/itly/hooks';
import withCart from 'components/PageWrapper/withCart';
import { getGatsbySiteUrl } from 'helpers/env';
import { useGoogleReCaptcha } from 'features/google-recaptcha';
import { useTikTok } from 'hooks/useTikTok';
import { SchemaOrgProduct } from 'components/SchemaOrg/SchemaOrgProduct';
import { DeviceSize, useWindowSize } from 'hooks/useWindowSize';
import { StickyCTA } from 'features/pdp/components/Form/StickyCTA';
import { Show } from 'components/Functional';
import { RelatedProducts } from 'components/RelatedProducts';
import { useRelatedProducts } from 'hooks/useRelatedProducts';
import { useSplitTreatment } from 'hooks/splits/useSplitTreatment';
import { SPLIT_IO_FEATURE_FLAG_MOBILE_PDP, SPLIT_IO_FEATURE_FLAG_MOBILE_PDP_COOKIE } from 'constants/split';
import { SPLIT_TRAFFIC_TYPES } from 'au-js-sdk/lib/services/split.io/constants';
import { all as allTrue } from 'helpers/conditionals';
import { useQueryParams } from 'hooks/useQueryParams';

export type PDPProduct = Product & {
  relatedProducts: RelatedProduct[];
  seoBlock?: string;
  bannerImage?: string;
};

interface PDPTemplateProps {
  data: {
    product: PDPProduct;
  };
  pageContext: {
    categoryName: string;
    breadcrumbs: [{ name: string; urlPath: string }];
    yotpoStars: Rating;
  };
  tracking: TrackingProp;
}

const DesktopBreadcrumbs = styled(Breadcrumbs)`
  display: none;
  ${mediaQuery.medium`
    display: flex;
  `};
`;

export const query = graphql`
  query Product($sku: String) {
    product(sku: { eq: $sku }) {
      productId
      ...BaseProduct
      seoBlock
      bannerImage
      customProductType
      tierPrices {
        price
        quantity
      }
      relatedProducts {
        position
        productSku
        productRelationType
        product {
          ...BaseProduct
        }
      }
    }
  }
`;

const isDesignService = (name) => name !== 'Wedding Album Design Services';

const PDPTemplate = ({ pageContext, data, tracking }: PDPTemplateProps) => {
  const { product } = data;
  const canonicalHref = `${getGatsbySiteUrl()}/${product.canonicalUrl || ''}`;
  const relatedProducts = useRelatedProducts(product);
  const { deviceSize } = useWindowSize();
  useTrackPageViewed(tracking, 'Product Description Page', product.name);
  useTrackProductViewed(tracking, product);
  useGoogleReCaptcha({
    pageType: 'Product Description Page',
    pageName: 'Product Description Page',
    action: 'pdp'
  });
  useTrackGleamEmailSubmitted(tracking);
  useTikTok({ event: 'PageView' });

  // Look for query param indicating we have come from a Google Shopping Ad
  const [gshop] = useQueryParams('gshop');
  const isMobile = deviceSize === DeviceSize.SMALL;
  const { treatmentStatus: mobilePdpTestEnabled } = useSplitTreatment(
    SPLIT_IO_FEATURE_FLAG_MOBILE_PDP,
    SPLIT_IO_FEATURE_FLAG_MOBILE_PDP_COOKIE,
    SPLIT_TRAFFIC_TYPES.ANONYMOUS
  );
  const showMobilePDPTest = allTrue(mobilePdpTestEnabled, !gshop, isMobile);

  return (
    <PDPProvider product={product}>
      <GlobalLayout
        displayPromoBanner
        displayProductAssistant
        width="1400px"
        bottomContent={
          <BottomContent
            breadcrumbs={pageContext.breadcrumbs}
            product={product}
            shouldHideBuyBanner={isDesignService(product.name)}
            showMobilePDPTest={showMobilePDPTest}
            relatedProducts={relatedProducts}
          />
        }
        templatePageContext="PDP"
      >
        <SEO
          title={product.metaTitle || product.name}
          description={product.metaDescription || product.description}
          keywords={[product.name]}
          ogType="og:product"
        />
        <SchemaOrgProduct product={product} pageContext={pageContext} />
        <Helmet link={[{ rel: 'canonical', href: canonicalHref }]} />
        <DesktopBreadcrumbs pages={pageContext.breadcrumbs} />
        <Show when={showMobilePDPTest}>
          <RelatedProducts products={relatedProducts} showMobilePDPTest={showMobilePDPTest} />
        </Show>
        <PDP yotpoStars={pageContext.yotpoStars} />
        {deviceSize === DeviceSize.SMALL && <StickyCTA />}
      </GlobalLayout>
    </PDPProvider>
  );
};

export default analyticsWrapper(withCart(PDPTemplate));
